import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { CoworkState } from '../../types/cowork';
import { formatClock, formatDuration } from '../../hooks/useCoworkAdmin';
import {
  ActionBar,
  Button,
  Grid,
  Input,
  Label,
  Muted,
  StatCard,
  StatValue,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
} from '../ui';

const Clock = styled.div<{ $warning?: boolean }>`
  font-family: ${theme.fonts.heading};
  font-size: clamp(3rem, 9vw, 5.5rem);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ $warning }) => ($warning ? theme.colors.pink : '#fff')};
  text-shadow: ${({ $warning }) => ($warning ? theme.colors.glow : 'none')};
  transition: color 0.3s ease;
`;

const PhaseTag = styled.span<{ $phase?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: ${({ $phase }) =>
    $phase === 'focus' ? 'rgba(255, 20, 147, 0.18)' :
    $phase === 'break' || $phase === 'long_break' ? 'rgba(76, 201, 240, 0.18)' :
    'rgba(255, 255, 255, 0.06)'};
  color: ${({ $phase }) =>
    $phase === 'focus' ? theme.colors.pink :
    $phase === 'break' || $phase === 'long_break' ? theme.colors.blue :
    theme.colors.textMuted};
  border: 1px solid currentColor;
`;

const QuickRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TaskLine = styled.div<{ $focused?: boolean; $done?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.55rem;
  border-radius: 8px;
  font-size: 0.86rem;
  background: ${({ $focused }) => ($focused ? 'rgba(255, 20, 147, 0.1)' : 'transparent')};
  border: 1px solid ${({ $focused }) => ($focused ? 'rgba(255, 20, 147, 0.3)' : 'transparent')};
  color: ${({ $done }) => ($done ? theme.colors.textMuted : '#fff')};
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};

  &:hover { background: rgba(255, 255, 255, 0.04); }
`;

const CommunityLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.32rem 0;
  font-size: 0.84rem;
  border-bottom: 1px solid rgba(255, 20, 147, 0.08);

  strong { color: ${theme.colors.pinkSoft}; font-weight: 600; }
  span { color: ${theme.colors.textMuted}; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const MiniField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  width: 82px;
`;

interface Props {
  state: CoworkState;
  remainingMs: number;
  run: (request: Promise<{ data: any }>) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const CoworkSessionTab: React.FC<Props> = ({ state, remainingMs, run, refresh }) => {
  const { timer } = state;
  const [quickTask, setQuickTask] = useState('');
  const [focusMinutes, setFocusMinutes] = useState(String(timer.focusMinutes));
  const [breakMinutes, setBreakMinutes] = useState(String(timer.breakMinutes));
  const [totalCycles, setTotalCycles] = useState(String(timer.totalCycles));
  const [savingConfig, setSavingConfig] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [extraCycles, setExtraCycles] = useState('1');
  const [addingCycles, setAddingCycles] = useState(false);

  const idle = timer.status === 'idle' || timer.status === 'finished';
  const warning = timer.status === 'running' && timer.phase === 'focus' && remainingMs <= 60000;

  const savedConfig = `${timer.focusMinutes}/${timer.breakMinutes}/${timer.totalCycles}`;
  const draftValues = [focusMinutes, breakMinutes, totalCycles].map((v) => parseInt(v, 10));
  const draftValid = draftValues.every((value) => Number.isFinite(value) && value > 0);
  const configChanged = draftValid && savedConfig !== draftValues.join('/');

  // Cuando cambia la config guardada (acá o desde Configuración) los campos la reflejan.
  useEffect(() => {
    const [focus, rest, cycles] = savedConfig.split('/');
    setFocusMinutes(focus);
    setBreakMinutes(rest);
    setTotalCycles(cycles);
  }, [savedConfig]);

  // Deja la config lista en el overlay sin tener que arrancar la sesión.
  const saveConfig = async () => {
    setSavingConfig(true);
    const ok = await run(api.put('/cowork/settings', {
      focusMinutes: parseInt(focusMinutes, 10),
      breakMinutes: parseInt(breakMinutes, 10),
      totalCycles: parseInt(totalCycles, 10),
    }));
    setSavingConfig(false);
    if (ok) await refresh();
  };

  const addCycles = async (count: number) => {
    if (!Number.isFinite(count) || count < 1) return;
    setAddingCycles(true);
    await run(api.post('/cowork/timer/add-cycles', { count }));
    setAddingCycles(false);
  };

  const addTask = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = quickTask.trim();
    if (!text) return;
    setQuickTask('');
    await run(api.post('/cowork/tasks', { text }));
    await refresh();
  };

  // Vacía el tablero de Euge y del chat para empezar una sesión desde cero.
  const clearTasks = async (includeCompleted: boolean) => {
    setClearing(true);
    const ok = await run(api.post('/cowork/tasks/clear', { scope: 'all', includeCompleted }));
    setClearing(false);
    setConfirmClear(false);
    if (ok) await refresh();
  };

  const pending = state.streamerTasks.filter((t) => t.status === 'pending');
  const communityPending = state.community.reduce((sum, row) => sum + (row.pendingCount || 0), 0);
  const visibleTasks = state.streamerTasks.filter((t) => t.status !== 'archived').slice(0, 8);

  return (
    <Grid $cols={2} style={{ alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Pomodoro</SurfaceTitle>
            <PhaseTag $phase={timer.phase || undefined}>
              {timer.status === 'finished'
                ? 'Sesión completa'
                : timer.status === 'idle'
                  ? 'Sin sesión'
                  : `${timer.phaseLabel}${timer.status === 'paused' ? ' · pausa' : ''}`}
            </PhaseTag>
          </SurfaceHeader>
          <SurfaceBody>
            <Clock $warning={warning}>{formatClock(remainingMs)}</Clock>
            <Muted>
              {timer.cycle > 0
                ? `Ciclo ${timer.cycle} de ${timer.totalCycles} · ${timer.focusCompleted} focus completados`
                : `Listo para ${timer.totalCycles} ciclos de ${timer.focusMinutes}/${timer.breakMinutes} min`}
            </Muted>

            {timer.status !== 'idle' && (
              <>
                <QuickRow style={{ marginTop: '0.65rem' }}>
                  <Button $variant="ghost" $size="sm" disabled={addingCycles} onClick={() => addCycles(1)}>
                    +1 ciclo
                  </Button>
                  <Button $variant="ghost" $size="sm" disabled={addingCycles} onClick={() => addCycles(2)}>
                    +2 ciclos
                  </Button>
                  <MiniField>
                    <Input
                      value={extraCycles}
                      onChange={(e) => setExtraCycles(e.target.value)}
                      inputMode="numeric"
                    />
                  </MiniField>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    disabled={addingCycles}
                    onClick={() => addCycles(parseInt(extraCycles, 10))}
                  >
                    Agregar
                  </Button>
                </QuickRow>
                <Muted style={{ marginTop: '0.35rem' }}>
                  Suma ciclos a esta sesión. Las tareas siguen en el tablero.
                  {timer.status === 'finished' ? ' Después, Continuar arranca el focus siguiente.' : ''}
                </Muted>
              </>
            )}

            <QuickRow style={{ marginTop: '0.85rem' }}>
              {idle && (
                <Button onClick={() => run(api.post('/cowork/timer/start', {
                  focusMinutes: parseInt(focusMinutes, 10),
                  breakMinutes: parseInt(breakMinutes, 10),
                  totalCycles: parseInt(totalCycles, 10),
                }))}>
                  {timer.status === 'finished' ? 'Nueva sesión' : 'Iniciar'}
                </Button>
              )}
              {timer.status === 'running' && (
                <Button onClick={() => run(api.post('/cowork/timer/pause'))}>Pausar</Button>
              )}
              {timer.status === 'paused' && (
                <Button onClick={() => run(api.post('/cowork/timer/resume'))}>Continuar</Button>
              )}
              {!idle && (
                <>
                  <Button $variant="ghost" onClick={() => run(api.post('/cowork/timer/skip'))}>
                    Siguiente
                  </Button>
                  <Button $variant="ghost" onClick={() => run(api.post('/cowork/timer/add-minutes', { minutes: 5 }))}>
                    +5 min
                  </Button>
                  <Button $variant="ghost" onClick={() => run(api.post('/cowork/timer/add-minutes', { minutes: -5 }))}>
                    −5 min
                  </Button>
                </>
              )}
            </QuickRow>

            <QuickRow style={{ marginTop: '0.5rem' }}>
              <Button $variant="ghost" $size="sm" onClick={() => run(api.post('/cowork/timer/phase', { phase: 'focus' }))}>
                Ir a focus
              </Button>
              <Button $variant="ghost" $size="sm" onClick={() => run(api.post('/cowork/timer/phase', { phase: 'break' }))}>
                Ir a descanso
              </Button>
              {timer.longBreakEnabled && (
                <Button $variant="ghost" $size="sm" onClick={() => run(api.post('/cowork/timer/phase', { phase: 'long_break' }))}>
                  Descanso largo
                </Button>
              )}
              <Button $variant="ghost" $size="sm" onClick={() => run(api.post('/cowork/timer/reset'))}>
                Reiniciar
              </Button>
              <Button $variant="danger" $size="sm" onClick={() => run(api.post('/cowork/timer/finish'))}>
                Finalizar
              </Button>
              {!confirmClear && (
                <Button $variant="ghost" $size="sm" onClick={() => setConfirmClear(true)}>
                  Borrar tareas
                </Button>
              )}
            </QuickRow>

            {confirmClear && (
              <>
                <QuickRow style={{ marginTop: '0.5rem' }}>
                  <Button $variant="danger" $size="sm" disabled={clearing} onClick={() => clearTasks(false)}>
                    Borrar pendientes
                  </Button>
                  <Button $variant="danger" $size="sm" disabled={clearing} onClick={() => clearTasks(true)}>
                    Borrar todo
                  </Button>
                  <Button $variant="ghost" $size="sm" disabled={clearing} onClick={() => setConfirmClear(false)}>
                    Cancelar
                  </Button>
                </QuickRow>
                <Muted style={{ marginTop: '0.35rem' }}>
                  Limpia tu lista y la del chat ({pending.length} tuyas y {communityPending} de la comunidad
                  pendientes). «Borrar todo» incluye también las completadas de hoy. El historial y las
                  estadísticas no se tocan.
                </Muted>
              </>
            )}

            {idle && (
              <QuickRow style={{ marginTop: '0.85rem' }}>
                <MiniField>
                  <Label style={{ margin: 0 }}>Focus</Label>
                  <Input value={focusMinutes} onChange={(e) => setFocusMinutes(e.target.value)} />
                </MiniField>
                <MiniField>
                  <Label style={{ margin: 0 }}>Descanso</Label>
                  <Input value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
                </MiniField>
                <MiniField>
                  <Label style={{ margin: 0 }}>Ciclos</Label>
                  <Input value={totalCycles} onChange={(e) => setTotalCycles(e.target.value)} />
                </MiniField>
                <QuickRow style={{ gap: '0.35rem', paddingTop: '1.1rem' }}>
                  {[[25, 5], [45, 10], [50, 10], [60, 15], [90, 20]].map(([f, b]) => (
                    <Button
                      key={`${f}-${b}`}
                      $variant="ghost"
                      $size="sm"
                      onClick={() => { setFocusMinutes(String(f)); setBreakMinutes(String(b)); }}
                    >
                      {f}/{b}
                    </Button>
                  ))}
                </QuickRow>
              </QuickRow>
            )}

            {idle && (
              <>
                <QuickRow style={{ marginTop: '0.6rem' }}>
                  <Button
                    $variant={configChanged ? undefined : 'ghost'}
                    $size="sm"
                    onClick={saveConfig}
                    disabled={savingConfig || !configChanged}
                  >
                    {savingConfig ? 'Guardando…' : 'Guardar configuración'}
                  </Button>
                </QuickRow>
                <Muted style={{ marginTop: '0.35rem' }}>
                  {configChanged
                    ? 'Los overlays siguen mostrando la config anterior hasta que la guardes.'
                    : 'Los overlays ya muestran esta configuración, sin necesidad de iniciar la sesión.'}
                </Muted>
              </>
            )}
          </SurfaceBody>
        </Surface>

        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Tarea actual</SurfaceTitle>
            {state.focusTask && (
              <Button $variant="ghost" $size="sm" onClick={() => run(api.post('/cowork/tasks/focus/clear'))}>
                Quitar foco
              </Button>
            )}
          </SurfaceHeader>
          <SurfaceBody>
            {state.focusTask ? (
              <>
                <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{state.focusTask.text}</div>
                <ActionBar style={{ marginTop: '0.6rem', justifyContent: 'flex-start' }}>
                  <Button
                    $size="sm"
                    onClick={async () => {
                      await run(api.put(`/cowork/tasks/${state.focusTask!.id}`, { status: 'completed' }));
                      const next = pending.find((t) => t.id !== state.focusTask!.id);
                      if (next) await run(api.post(`/cowork/tasks/${next.id}/focus`));
                    }}
                  >
                    Completar y pasar a la siguiente
                  </Button>
                </ActionBar>
              </>
            ) : (
              <Muted>Elegí una tarea de la lista para ponerla en foco.</Muted>
            )}
          </SurfaceBody>
        </Surface>

        <Grid $cols={2}>
          <StatCard>
            <Label style={{ margin: 0 }}>Focus de hoy</Label>
            <StatValue style={{ fontSize: '1.4rem' }}>{formatDuration(state.stats.focusMs)}</StatValue>
          </StatCard>
          <StatCard>
            <Label style={{ margin: 0 }}>Ciclos</Label>
            <StatValue style={{ fontSize: '1.4rem' }}>{state.stats.cycles}</StatValue>
          </StatCard>
          <StatCard>
            <Label style={{ margin: 0 }}>Participantes</Label>
            <StatValue style={{ fontSize: '1.4rem' }}>{state.stats.participants}</StatValue>
          </StatCard>
          <StatCard>
            <Label style={{ margin: 0 }}>Tareas de la comunidad</Label>
            <StatValue style={{ fontSize: '1.4rem' }}>{state.stats.communityCompleted}</StatValue>
          </StatCard>
        </Grid>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Mis tareas</SurfaceTitle>
            <Muted style={{ margin: 0 }}>{pending.length} pendientes</Muted>
          </SurfaceHeader>
          <SurfaceBody>
            <form onSubmit={addTask}>
              <Input
                value={quickTask}
                onChange={(e) => setQuickTask(e.target.value)}
                placeholder="Escribí una tarea y apretá Enter"
              />
            </form>

            <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              {visibleTasks.length === 0 && <Muted>Todavía no cargaste tareas.</Muted>}
              {visibleTasks.map((task) => (
                <TaskLine key={task.id} $focused={task.focused} $done={task.status === 'completed'}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {task.focused ? '● ' : task.status === 'completed' ? '✓ ' : '○ '}
                    {task.text}
                  </span>
                  {task.status === 'pending' && !task.focused && (
                    <Button $variant="ghost" $size="sm" onClick={() => run(api.post(`/cowork/tasks/${task.id}/focus`))}>
                      Foco
                    </Button>
                  )}
                  <Button
                    $variant="ghost"
                    $size="sm"
                    onClick={() => run(api.put(`/cowork/tasks/${task.id}`, {
                      status: task.status === 'completed' ? 'pending' : 'completed',
                    }))}
                  >
                    {task.status === 'completed' ? 'Reabrir' : 'Listo'}
                  </Button>
                </TaskLine>
              ))}
            </div>
          </SurfaceBody>
        </Surface>

        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Actividad del chat</SurfaceTitle>
            <Muted style={{ margin: 0 }}>{state.community.length} en cowork</Muted>
          </SurfaceHeader>
          <SurfaceBody>
            {state.community.length === 0 && (
              <Muted>Nadie cargó tareas todavía. En el chat: !tarea &lt;texto&gt;</Muted>
            )}
            {state.community.slice(0, 12).map((row) => (
              <CommunityLine key={row.twitchId}>
                <strong>{row.displayName}</strong>
                <span>
                  {row.focusText || `${row.pendingCount} pendiente${row.pendingCount === 1 ? '' : 's'}`}
                </span>
                <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{row.completedCount} ✓</span>
              </CommunityLine>
            ))}
          </SurfaceBody>
        </Surface>
      </div>
    </Grid>
  );
};

export default CoworkSessionTab;
